<script async setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from "vue";

const props = defineProps({
    active: {
        type: Boolean,
        default: false,
    },
    text: {
        type: String,
        default: "",
    },
    speed: {
        type: [String, Number],
        default: 80,
    },
});

const containerRef = ref(null);
const clipRef = ref(null);
const tickerRef = ref(null);

const containerWidth = ref(0);
const tickerWidth = ref(0);
const animationDuration = ref(20);

let resizeObserver = null;

const updateSizes = async () => {
    await nextTick();

    containerWidth.value = clipRef.value?.offsetWidth || 0;
    tickerWidth.value = tickerRef.value?.offsetWidth || 0;

    const distance = containerWidth.value + tickerWidth.value;
    const pixelsPerSecond = Math.max(1, Number(props.speed) || 80);

    animationDuration.value = distance / pixelsPerSecond;
};

onMounted(async () => {
    await updateSizes();

    resizeObserver = new ResizeObserver(() => {
        updateSizes();
    });

    if (containerRef.value) {
        resizeObserver.observe(containerRef.value);
    }

    if (clipRef.value) {
        resizeObserver.observe(clipRef.value);
    }

    if (tickerRef.value) {
        resizeObserver.observe(tickerRef.value);
    }
});

onBeforeUnmount(() => {
    if (resizeObserver) {
        resizeObserver.disconnect();
    }
});

watch(() => props.text, updateSizes);
watch(() => props.active, updateSizes);
watch(() => props.speed, updateSizes);
</script>

<template>
    <div
        v-if="props.active"
        class="absolute top-14.5 notebook:top-25.5 left-0 w-full bg-black text-md font-medium text-white h-9"
    >
        <div
            ref="containerRef"
            class="max-w-ts-content mx-auto px-0 mini:px-2 size-full relative"
        >
            <div
                ref="clipRef"
                class="size-full overflow-hidden px-4 relative"
            >
                <div
                    ref="tickerRef"
                    class="ticker absolute whitespace-nowrap left-0 top-1/2"
                    :style="{
                        animationDuration: animationDuration + 's',
                        '--start-x': containerWidth + 'px',
                        '--end-x': '-' + tickerWidth + 'px',
                    }"
                >
                    <span class="text-sm phone:text-base inline-block">
                        {{ props.text }}
                    </span>
                </div>

                <div class="ticker-fade ticker-fade-left"></div>
                <div class="ticker-fade ticker-fade-right"></div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.ticker {
    animation: ticker linear infinite;
    transform: translate(var(--start-x), -50%);
    will-change: transform;
}

.bg-black:hover .ticker {
    animation-play-state: paused;
}

.ticker-fade {
    position: absolute;
    left: auto;
    top: 50%;
    transform: translateY(-50%);
    height: 90%;
    width: 40px;
    pointer-events: none;
    z-index: 2;
}

.ticker-fade-left {
    left: 0;
    background: linear-gradient(to right, #000 0%, rgba(0, 0, 0, 0) 100%);
}

.ticker-fade-right {
    right: 0;
    background: linear-gradient(to left, #000 0%, rgba(0, 0, 0, 0) 100%);
}

@keyframes ticker {
    from {
        transform: translate(var(--start-x), -50%);
    }
    to {
        transform: translate(var(--end-x), -50%);
    }
}

@media (max-width: 949px) {
    .ticker-fade {
        display: none;
    }
}
</style>